import { Badge } from "@/components/ui/badge";
import { useApi } from "@/hooks/use-api";
import { Course } from "@/types/course";
import { ClipboardList, TextQuote, UsersRound } from "lucide-react";
import { useEffect, useState } from "react";
import { CourseDialog } from "../CourseDialog";
import { Enrollment } from "./Enrollment";
import { Overview } from "./Overview";
import { ShareCourseDialog } from "./ShareCourseDialog";
import { StudentPortal } from "./StudentPortal";
import { Student } from "./StudentsTable";

interface CoursePageProps {
  course: Course;
}

export const CoursePage = ({ course }: CoursePageProps) => {
  const { fetchCourseStudents } = useApi();
  const [students, setStudents] = useState<Student[]>([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editCourseDialogOpen, setEditCourseDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const data = await fetchCourseStudents(course.id);
        setStudents(data);
      } catch (error) {
        console.error("Failed to fetch students", error);
      }
    };
    fetchStudents();
  }, [course.id, fetchCourseStudents]);

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{course.reference}</span>
            <span>&gt;</span>
            <span>{course.semester}</span>

            <div className="flex items-center justify-center gap-2 ml-4">
              <Badge
                variant={activeTab === "overview" ? "default" : "secondary"}
                className="cursor-pointer text-xs font-normal"
                onClick={() => setActiveTab("overview")}
              >
                <ClipboardList />
                Overview
              </Badge>
              <Badge
                variant={activeTab === "student-portal" ? "default" : "secondary"}
                className="cursor-pointer text-xs font-normal"
                onClick={() => setActiveTab("student-portal")}
              >
                <TextQuote />
                Student Portal
              </Badge>
              <Badge
                variant={activeTab === "enrollment" ? "default" : "secondary"}
                className="cursor-pointer text-xs font-normal"
                onClick={() => setActiveTab("enrollment")}
              >
                <UsersRound />
                Enrollment
              </Badge>
            </div>
          </div>
        </div>

        <div className="mt-6">
          {activeTab === "overview" && (
            <Overview
              course={course}
              studentCount={students.length}
              onEdit={() => setEditCourseDialogOpen(true)}
            />
          )}

          {activeTab === "student-portal" && (
            <StudentPortal
              course={course}
              studentCount={students.length}
              onShare={() => setShareDialogOpen(true)}
            />
          )}

          {activeTab === "enrollment" && (
            <Enrollment
              course={course}
              students={students}
              onShare={() => setShareDialogOpen(true)}
            />
          )}

        </div>
      </div>

      <ShareCourseDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        course={course}
      />

      <CourseDialog
        isOpen={editCourseDialogOpen}
        onClose={() => setEditCourseDialogOpen(false)}
        course={course}
      />
    </div>
  );
};
